import React, { useContext, useEffect, useRef, useState } from "react";
import StepContainer from "./StepContainer";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  bodyTextSx,
  dividerWhiteSx,
  FormBody,
  FormElement,
  formFieldSetLargeSx,
  PrimaryColorSpan,
  submittedMobileNumberSx,
  VerificationCodeInput,
} from "../SignUpForm.styles";
import { Form } from "react-final-form";
import PhoneInput, {
  TwoStepPhoneInputHandle,
} from "../../../common/TwoStepPhoneInput";
import { OnboardingContext } from "../../../../store/OnboardingContext";
import { Alert, AlertTitle } from "@mui/material";
import {
  SecondaryButton,
  SmsButton,
  PrimaryButton,
} from "../../../common/buttons";
import { Box, Divider } from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import { GOOGLE_CAPTCHA_SITE_KEY } from "../../../../env";
import { validatePhoneClientSide } from "../../../../utils/PhoneValidator";
import {
  PhoneValidationSuggestion,
  maskE164,
} from "../../../../utils/errorMessages";
import useLogger from "../../../../hooks/useLogger";
import {
  LogEventCategory,
  AccountActionEvents,
} from "../../../../api/entities/Log";

/**
 * PM5 T2 (money path, batch "onboarding") — converted from MUI v4 to MUI v5+
 * (`@mui/material`). See docs/mui-conversion-guide.md.
 *
 * **AUTH ESCALATION CLASS.** Phone capture + OTP verification for signup —
 * captcha gating, the #284 client-side phone validation, the SMS send/resend
 * paths and the `[phone-validation]` CloudWatch audit trail all live here.
 * Styling-only conversion: no handler, guard, log payload or ref plumbing
 * moved. `Alert`/`AlertTitle` move from `@material-ui/lab` to `@mui/material`
 * core (guide §1.4).
 *
 * Every `<Box mt={1}>` / `<Box px={2} pt={1}>` shorthand becomes an `sx` key —
 * v9's `createBox` reads only `sx` and forwards anything else to the DOM, so
 * the shorthands would silently apply NOTHING (guide §2, the invisible one).
 */

// v4's `<Grid container spacing={2}>` declarations, carried verbatim rather
// than re-derived against v9's `gap` implementation (guide §2, and the same
// treatment as SignIn/EmailSignIn.tsx).
const v4GridContainerSx: SxProps<Theme> = {
  width: "calc(100% + 16px)",
  margin: "-8px",
};
const v4GridItemSx: SxProps<Theme> = { padding: "8px" };

// v9's `Grid` types its `sx` as a single `SystemStyleObject`, not `SxProps`, so
// the `sx={[a, b]}` array form does not compile on a Grid — the item padding and
// the shared `formFieldSetLarge` rule are merged into one object, in the order
// the two v4 classes were declared.
const phoneFieldItemSx: SxProps<Theme> = {
  padding: "8px",
  ...formFieldSetLargeSx,
};

// The two `<Grid item xs={12} sm={12}>` in the verification branch sit inside a
// plain <form> with no `<Grid container>` ancestor — port as a Box carrying the
// literal v4 declarations, never as a v9 Grid (guide §2).
const orphanGridItemSx: SxProps<Theme> = {
  boxSizing: "border-box",
  margin: 0,
  flexGrow: 0,
  flexBasis: "100%",
  maxWidth: "100%",
};

const SignUpStepPhone = () => {
  const {
    application,
    loading,
    setPhone,
    verifyPhone,
    runWithLoader,
    phoneSuggestion: serverSuggestion,
    resetPhoneSuggestion,
  } = useContext(OnboardingContext);
  const { logEvent } = useLogger();
  const [countryCode, setCountryCode] = useState<string>();
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [changingPhoneNumber, setChangingPhoneNumber] = useState(false);
  // Imperative ref to TwoStepPhoneInput. Used to seed the input field
  // when the user applies a #284 phone-validation suggestion. Going via
  // the component's exposed `seedValue` rather than passing our own DOM
  // ref avoids overriding react-phone-input-2's internal ref (which the
  // library needs for focus / cursor management — overriding it crashes
  // the lib with "Cannot read properties of undefined (reading 'focus')"
  // and trips the app-level ErrorBoundary, blanking the page).
  const phoneInputRef = useRef<TwoStepPhoneInputHandle | null>(null);
  const [resent, setResent] = useState(false);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [showCodeHelp, setShowCodeHelp] = useState(false);
  // Client-side phone validation state (#284). `clientError` carries the
  // user-facing message when client validation rejects the input;
  // `clientSuggestion` carries any "Use this number" correction we can
  // offer. Server-side rejections come through `serverSuggestion` from
  // the OnboardingContext and are merged into the same UI.
  const [clientError, setClientError] = useState<string | undefined>();
  const [clientSuggestion, setClientSuggestion] = useState<
    PhoneValidationSuggestion | undefined
  >();

  // Google ReCAPTCHA — visible checkbox for initial submit, invisible for resend
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);
  const invisibleRecaptchaRef = React.useRef<ReCAPTCHA>(null);

  const savedCountryCode = application?.countryCode;
  const savedPhoneNumber = application?.phoneNumber;

  const verifying =
    !changingPhoneNumber && savedCountryCode && savedPhoneNumber;

  useEffect(() => {
    if (!verifying) {
      setShowCodeHelp(false);
      return;
    }
    const timer = setTimeout(() => setShowCodeHelp(true), 45000);
    return () => clearTimeout(timer);
  }, [verifying, resent]);

  // Log server-side rejections that come back via OnboardingContext.
  // Mirrors the equivalent path in MobileSignIn so signup + login share
  // the same `[phone-validation]` audit trail in CloudWatch.
  useEffect(() => {
    if (!serverSuggestion) return;
    logEvent({
      category: LogEventCategory.ACCOUNT_ACTION,
      event: AccountActionEvents.PHONE_VALIDATION_REJECTED,
      data: {
        surface: "signup",
        source: "server",
        rejectionReason: serverSuggestion.rejectionReason,
        code: serverSuggestion.code,
        suggestedE164: serverSuggestion.suggestedE164
          ? maskE164(serverSuggestion.suggestedE164)
          : undefined,
      },
    });
  }, [serverSuggestion, logEvent]);

  if (!application) return null;

  const activeSuggestion = clientSuggestion || serverSuggestion;

  const onPhoneChange = (nextCountryCode: string, nextPhoneNumber: string) => {
    setCountryCode(nextCountryCode || undefined);
    setPhoneNumber(nextPhoneNumber.replace(/^0+/, "").replace(/\D/g, ""));
    setClientError(undefined);
    setClientSuggestion(undefined);
    if (serverSuggestion) resetPhoneSuggestion();
    if (nextCountryCode || nextPhoneNumber) setShowCaptcha(true);
  };

  const applySuggestion = () => {
    if (!activeSuggestion) return;
    const {
      suggestedPhoneNumber,
      suggestedCallingCode,
      suggestedE164,
      rejectionReason,
    } = activeSuggestion;
    if (suggestedPhoneNumber) setPhoneNumber(suggestedPhoneNumber);
    if (suggestedCallingCode) setCountryCode(suggestedCallingCode);

    // Seed the input via TwoStepPhoneInput's imperative `seedValue` API.
    // It locates the underlying input via querySelector so we don't have
    // to hand react-phone-input-2 a ref (passing one collapses the
    // library's own ref → focus / cursor crash, white screen).
    if (suggestedE164) phoneInputRef.current?.seedValue(suggestedE164);

    logEvent({
      category: LogEventCategory.ACCOUNT_ACTION,
      event: AccountActionEvents.PHONE_VALIDATION_SUGGESTION_APPLIED,
      data: {
        surface: "signup",
        source: clientSuggestion ? "client" : "server",
        rejectionReason,
        suggestedE164: suggestedE164 ? maskE164(suggestedE164) : undefined,
      },
    });
    setClientError(undefined);
    setClientSuggestion(undefined);
    if (serverSuggestion) resetPhoneSuggestion();
  };

  const changeMobileNumber = () => {
    setChangingPhoneNumber(true);
  };

  const resend = (sendMethod: "sms" | "whatsapp") => () => {
    if (!canSubmit || !countryCode || !phoneNumber) return;

    setResent(false);

    runWithLoader(
      async () => {
        const token = await invisibleRecaptchaRef.current?.executeAsync();
        invisibleRecaptchaRef.current?.reset();
        if (!token) {
          throw new Error("Could not verify request — please try again.");
        }
        setPhone(countryCode, phoneNumber, token);
        setResent(true);
      },
      true,
      "phone-send",
    );
  };

  const canSubmit =
    countryCode &&
    countryCode.length > 0 &&
    phoneNumber &&
    phoneNumber.length > 6;

  // Submit Form
  const submit = (sendMethod: "sms" | "whatsapp") => async () => {
    if (!canSubmit || !countryCode || !phoneNumber) return;

    // Client-side phone validation (#284). Runs before captcha + API call
    // so obvious typos (double country code, length issues) are caught
    // without paying for a network round-trip. Server is the backstop.
    const clientCheck = validatePhoneClientSide(countryCode, phoneNumber);
    if (!clientCheck.valid) {
      setClientError(clientCheck.userMessage);
      setClientSuggestion(clientCheck.suggestion);
      logEvent({
        category: LogEventCategory.ACCOUNT_ACTION,
        event: AccountActionEvents.PHONE_VALIDATION_REJECTED,
        data: {
          surface: "signup",
          source: "client",
          rejectionReason: clientCheck.suggestion?.rejectionReason,
          suggestedE164: clientCheck.suggestion?.suggestedE164
            ? maskE164(clientCheck.suggestion.suggestedE164)
            : undefined,
        },
      });
      return;
    }

    if (!recaptchaRef.current) {
      console.log("Error initializing recaptcha");
      return;
    }
    const captchaToken = recaptchaRef.current.getValue();

    if (!captchaToken) {
      setCaptchaRequired(true);
      console.log("Error Occurred, please try again.");
      return;
    }
    setChangingPhoneNumber(false);
    setPhone(countryCode, phoneNumber, captchaToken);
    recaptchaRef.current.reset();
  };

  return (
    <StepContainer>
      <StepContainer.Body>
        {!verifying && (
          <Form onSubmit={submit("sms")}>
            {(props) => (
              <form onSubmit={props.handleSubmit} autoComplete="false">
                <FormBody>
                  <Grid container sx={v4GridContainerSx}>
                    <Grid size={{ xs: 12 }} sx={v4GridItemSx}>
                      <Typography
                        variant="h6"
                        sx={bodyTextSx}
                        align="center"
                        style={{
                          fontWeight: 500,
                          marginTop: "24px",
                          color: "#eee",
                          lineHeight: "1.4rem",
                        }}
                      >
                        We <PrimaryColorSpan>send a code</PrimaryColorSpan> to
                        your phone to verify it&apos;s you. Please select your
                        phone <PrimaryColorSpan>country code</PrimaryColorSpan>{" "}
                        and type your{" "}
                        <PrimaryColorSpan>number</PrimaryColorSpan> below
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12 }} sx={phoneFieldItemSx}>
                      <PhoneInput
                        ref={phoneInputRef}
                        onChange={onPhoneChange}
                        disabled={loading}
                      />
                      {clientError && (
                        <Alert
                          severity="error"
                          variant="filled"
                          style={{ marginTop: "12px" }}
                        >
                          <AlertTitle>
                            Please check your phone number
                          </AlertTitle>
                          {clientError}
                        </Alert>
                      )}
                      {activeSuggestion && activeSuggestion.suggestedE164 && (
                        <Box sx={{ mt: 1 }}>
                          <PrimaryButton
                            title={`Use ${activeSuggestion.suggestedE164}`}
                            onClick={applySuggestion}
                          />
                        </Box>
                      )}
                    </Grid>
                    {showCaptcha && (
                      <Grid
                        size={{ xs: 12, sm: 12 }}
                        sx={v4GridItemSx}
                        style={{ textAlign: "center" }}
                      >
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          size="normal"
                          theme="dark"
                          sitekey={GOOGLE_CAPTCHA_SITE_KEY}
                        />
                        {captchaRequired && (
                          <Alert
                            severity="error"
                            variant="filled"
                            onClose={() => setCaptchaRequired(false)}
                          >
                            Please complete Captcha
                          </Alert>
                        )}
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                      <SmsButton
                        title="Continue with SMS"
                        disabled={!canSubmit}
                        onClick={submit("sms")}
                      />
                      {/* <WhatsappButton
                        title="Continue with WhatsApp"
                        disabled={!canSubmit}
                        onClick={submit("whatsapp")}
                      /> */}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                      <Typography
                        variant="body2"
                        style={{
                          textAlign: "center",
                          color: "#ddd",
                          fontSize: "0.9rem",
                          lineHeight: "0.9rem",
                          marginTop: "12px",
                          fontWeight: 600,
                        }}
                      >
                        Don&apos;t know your Country Code?{" "}
                        <a
                          href="https://countrycode.org/"
                          title="Check Your Country Code here"
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "#99ca3c" }}
                        >
                          Check here
                        </a>
                      </Typography>
                    </Grid>
                  </Grid>
                </FormBody>
              </form>
            )}
          </Form>
        )}
        {verifying && (
          <>
            <Typography
              variant="h3"
              sx={bodyTextSx}
              align="center"
              style={{
                fontWeight: 500,
                marginTop: "24px",
                marginBottom: "12px",
                color: "#eee",
              }}
            >
              Verify Phone
            </Typography>
            <Typography
              variant="body2"
              sx={bodyTextSx}
              align="center"
              style={{ fontWeight: 400, color: "#999", lineHeight: "1rem" }}
            >
              Enter the OTP (One Time Passcode) we&apos;ve sent to your phone
              number.
            </Typography>
            {resent && (
              <Alert
                severity="success"
                variant="filled"
                onClose={() => setResent(false)}
              >
                Verification code resent successfully!
              </Alert>
            )}
            <Typography variant="body1" sx={submittedMobileNumberSx}>
              {`+${countryCode} ${phoneNumber}`}
            </Typography>

            <FormElement>
              <ReCAPTCHA
                ref={invisibleRecaptchaRef}
                size="invisible"
                sitekey={GOOGLE_CAPTCHA_SITE_KEY}
              />
              <Box sx={orphanGridItemSx}>
                <VerificationCodeInput
                  type="number"
                  fields={6}
                  onComplete={verifyPhone}
                  disabled={loading}
                />
              </Box>
              <Box sx={orphanGridItemSx}>
                <Box sx={{ px: 2, pt: 1 }}>
                  <SmsButton
                    title="Resend Code with SMS"
                    onClick={resend("sms")}
                  />
                  {/* <WhatsappButton
                    title="Resend Code with WhatsApp"
                    onClick={resend("whatsapp")}
                  /> */}

                  <Box sx={{ px: 2, pb: 2 }}>
                    <Divider sx={dividerWhiteSx} />
                  </Box>

                  <SecondaryButton
                    title="Change Phone"
                    onClick={changeMobileNumber}
                  />
                  {showCodeHelp && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info" variant="outlined">
                        <Typography variant="body2" style={{ color: "#eee" }}>
                          {"Still waiting for your code? Try these steps:"}
                        </Typography>
                        <ul
                          style={{
                            margin: "8px 0",
                            paddingLeft: "20px",
                            color: "#ccc",
                          }}
                        >
                          <li>
                            {
                              "Check your phone has signal and isn't blocking unknown numbers"
                            }
                          </li>
                          <li>{"Tap Resend Code above to try again"}</li>
                          <li>
                            {"Tap Change Phone if you entered the wrong number"}
                          </li>
                          <li>
                            {"If nothing works, email us at "}
                            <a
                              href="mailto:help@farmflix.tv"
                              style={{ color: "#99ca3c" }}
                            >
                              {"help@farmflix.tv"}
                            </a>
                          </li>
                        </ul>
                      </Alert>
                    </Box>
                  )}
                </Box>
              </Box>
            </FormElement>
          </>
        )}
      </StepContainer.Body>
      <StepContainer.Actions />
    </StepContainer>
  );
};

export default SignUpStepPhone;
